// Utils
import { pluralizeString } from '@helpers/index';
import text from '@translations/en.json';

/**
 * Class to format numeric values into different string formats
 */
class NumericFormatter {
  private number: number;

  /**
   * Numeric value to be formatted
   * @param number Numeric value to format
   */
  constructor(number: number) {
    this.number = number;
  }

  /**
   * Formats the number into a pretty value string
   *
   * @function
 * @param {number} value Numeric value to format

   * @param {number} fractionDigits Amount of digits to use
   * @returns Formatted string
   */
  private formatValue = (value: number, fractionDigits = 0): string =>
    Math.abs(value).toLocaleString(undefined, {
      maximumFractionDigits: fractionDigits,
      minimumFractionDigits: fractionDigits,
    });

  /**
   * Formats the number into a pretty currency string
   *
   * @function
   * @param {number} fractionDigits Amount of digits to use
   * @returns Formatted string
   */
  public asCurrency = (fractionDigits = 0): string =>
    Math.abs(this.number).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: fractionDigits,
      minimumFractionDigits: fractionDigits,
    });

  /**
   * Formats the number into a pretty value string
   *
   * @function
   * @param {number} fractionDigits Amount of digits to use
   * @returns Formatted string
   */
  public asValue = (fractionDigits = 0): string => this.formatValue(this.number, fractionDigits);

  /**
   * Formats the number into a pretty value string adding the 'TEU' units at the end
   *
   * @function
   * @param {number} fractionDigits Amount of digits to use
   * @returns Formatted string
   */
  public teu = (fractionDigits = 0): string => `${this.asValue(fractionDigits)} ${text.teu}`;

  /**
   * Formats the number into a pretty value string adding the 'MT' units at the end
   *
   * @function
   * @param {number} fractionDigits Amount of digits to use
   * @returns Formatted string
   */
  public mt = (fractionDigits = 0): string => `${this.asValue(fractionDigits)} ${text.mt}`;

  /**
   * Formats the number into a pretty value string adding the 'NM' units at the end
   *
   * @function
   * @param {number} fractionDigits Amount of digits to use
   * @returns Formatted string
   */
  public nm = (fractionDigits = 0): string => `${this.asValue(fractionDigits)} ${text.nm}`;

  /**
   * Formats the number into a pretty value string adding the 'kn' units at the end
   *
   * @function
   * @param {number} fractionDigits Amount of digits to use
   * @returns Formatted string
   */
  public kn = (fractionDigits = 1): string => `${this.asValue(fractionDigits)} ${text.kn}`;

  /**
   * Formats the number into a pretty currency string adding the 'Per MT' units at the end
   *
   * @function
   * @param {string} fuel fuel string to display
   * @param {number} fractionDigits Amount of digits to use
   * @returns Formatted string
   */
  public fuelMT = (fuel: string, fractionDigits = 0): string =>
    `${fuel.toUpperCase()} - ${this.asCurrency(fractionDigits)} ${text.per_mt}`;

  /**
   * Formats the number into a pretty currency string adding the fuel type at the end
   *
   * @function
   * @param {string} fuel fuel string to display
   * @param {number} fractionDigits Amount of digits to use
   * @returns Formatted string
   */
  public fuelPrice = (fuel: string, fractionDigits = 0): string =>
    `${this.asCurrency(fractionDigits)} - ${fuel.toUpperCase()}`;

  /**
   * Formats the number into a pretty value string percentage
   *
   * @function
   * @param {number} percentage Percentage to display
   * @param {number} fractionDigits Amount of digits to use
   * @returns Formatted string
   */
  public percentage = (fractionDigits = 0): string => {
    const formattedPercentage = this.asValue(fractionDigits);
    return `${formattedPercentage}%`;
  };

  /**
   * Formats the number into a pretty value string adding the fuel type and the percentage
   *
   * @function
   * @param {string} fuel fuel string to display
   * @param {number} percentage Percentage to display
   * @param {number} fractionDigits Amount of digits to use
   * @returns Formatted string
   */
  public fuelProportion = (fuel: string, percentage: number, fractionDigits = 0): string => {
    const formattedPercentage = this.formatValue(percentage);
    const formattedValue = this.asValue(fractionDigits);
    const fuelType = fuel.toUpperCase();

    return `${formattedPercentage}% ${fuelType} (${formattedValue} ${text.mt})`;
  };

  /**
   * Formats the number into a pretty value string adding the correct compliance string
   *
   * @function
   * @param {number} fractionDigits Amount of digits to use
   * @returns Formatted string
   */
  public compliance = (fractionDigits = 0): string => {
    const formattedValue = this.asValue(fractionDigits || 2);
    const complianceText = this.number > 0 ? text.from_compliance : text.from_non_compliance;
    return `${formattedValue} ${complianceText}`;
  };

  /**
   * Formats the number into days and hours value
   *
   * @function
   * @returns Formatted string
   */
  public daysAndHours = (): string => {
    const sign = this.number >= 0 ? '+' : '-';
    const totalHours = Math.abs(this.number);
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;

    const dayString = pluralizeString(text.day, days);
    const hourString = hours > 0 ? ` ${hours} ${text.hrs}` : '';

    return `${sign} ${days} ${dayString} ${hourString}`;
  };
}

export default NumericFormatter;

/**
 * Formats a numeric value into a pretty value string
 *
 * @function
 * @param {number} value Numeric value to format
 * @param {number} fractionDigits Amount of digits to use
 * @returns Formatted string
 */
export const formatValue = (value: number, fractionDigits = 0): string =>
  new NumericFormatter(value).asValue(fractionDigits);

/**
 * Formats a numeric value into a pretty currency string
 *
 * @function
 * @param {number} value Numeric value to format
 * @param {number} fractionDigits Amount of digits to use
 * @returns Formatted string
 */
export const formatCurrency = (value: number, fractionDigits = 0): string =>
  new NumericFormatter(value).asCurrency(fractionDigits);

/**
 * Formats a numeric value into a pretty value string adding the 'TEU' units at the end
 *
 * @function
 * @param {number} value Numeric value to format
 * @param {number} fractionDigits Amount of digits to use
 * @returns Formatted string
 */
export const formatTEU = (value: number, fractionDigits = 0): string => new NumericFormatter(value).teu(fractionDigits);

/**
 * Formats a numeric value into a pretty value string adding the 'MT' units at the end
 *
 * @function
 * @param {number} value Numeric value to format
 * @param {number} fractionDigits Amount of digits to use
 * @returns Formatted string
 */
export const formatMT = (value: number, fractionDigits = 0): string => new NumericFormatter(value).mt(fractionDigits);

/**
 * Formats a numeric value into a pretty value string adding the 'NM' units at the end
 *
 * @function
 * @param {number} value Numeric value to format
 * @param {number} fractionDigits Amount of digits to use
 * @returns Formatted string
 */
export const formatNM = (value: number, fractionDigits = 0): string => new NumericFormatter(value).nm(fractionDigits);

/**
 * Formats a numeric value into a pretty value string adding the 'kn' units at the end
 *
 * @function
 * @param {number} value Numeric value to format
 * @param {number} fractionDigits Amount of digits to use
 * @returns Formatted string
 */
export const formatKN = (value: number, fractionDigits = 1): string => new NumericFormatter(value).kn(fractionDigits);

/**
 * Formats a numeric value into a pretty currency string adding the 'Per MT' units at the end
 *
 * @function
 * @param {string} fuel fuel string to display
 * @param {number} value Numeric value to format
 * @param {number} fractionDigits Amount of digits to use
 * @returns Formatted string
 */
export const formatFuelMT = (fuel: string, value: number, fractionDigits = 0): string =>
  new NumericFormatter(value).fuelMT(fuel, fractionDigits);

/**
 * Formats a numeric value into a pretty currency string adding the fuel type at the end
 *
 * @function
 * @param {string} fuel fuel string to display
 * @param {number} value Numeric value to format
 * @param {number} fractionDigits Amount of digits to use
 * @returns Formatted string
 */
export const formatFuelPrice = (fuel: string, value: number, fractionDigits = 0): string =>
  new NumericFormatter(value).fuelPrice(fuel, fractionDigits);

/**
 * Formats a numeric value into a pretty value string adding the fuel type and the percentage
 *
 * @function
 * @param {string} fuel fuel string to display
 * @param {number} value Numeric value to format
 * @param {number} percentage Percentage to display
 * @param {number} fractionDigits Amount of digits to use
 * @returns Formatted string
 */
export const formatFuelProportion = (fuel: string, value: number, percentage: number, fractionDigits = 0): string =>
  new NumericFormatter(value).fuelProportion(fuel, percentage, fractionDigits);

/**
 * Formats a numeric value into a pretty value string adding the correct compliance string
 *
 * @function
 * @param {number} value Numeric value to format
 * @param {number} fractionDigits Amount of digits to use
 * @returns Formatted string
 */
export const formatCompliance = (value: number, fractionDigits = 0): string =>
  new NumericFormatter(value).compliance(fractionDigits);

/**
 * Formats a numeric value into days and hours value
 *
 * @function
 * @param {number} value Numeric value to format
 * @returns Formatted string
 */
export const formatDaysAndHours = (value: number): string => new NumericFormatter(value).daysAndHours();

/**
 * Formats the number into a pretty value string percentage
 *
 * @function
 * @param {number} value Numeric value to format
 * @param {number} fractionDigits Amount of digits to use
 * @returns Formatted string
 */
export const formatPercentage = (value: number, fractionDigits = 0): string =>
  new NumericFormatter(value).percentage(fractionDigits);
